import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-unificator/services';
import { useStore } from 'ngx-unificator/store';
import { timer } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ModalService } from '../modal/modal.service';
import { GlobalStoreName } from '../store';
import { StickyBannerStoreEntity } from '../store/sticky-banner.store';
import { StickyBannersState, StickyModalConfig, StickyModalConfigKey, StickyModalQueueCookie, StickyModalQueueItem } from '../types/sticky-banner.type';
import { CustomNotificationCenterService } from '../shared/modules/notification-center/custom-notification-center.service';

@Injectable({
  providedIn: 'root',
})
export class StickyBannerService {
  private readonly _cookie = inject(CookieService);
  private readonly _modal = inject(ModalService);
  private readonly _customNotify = inject(CustomNotificationCenterService);
  private readonly _stickyBannersStore = useStore<StickyBannerStoreEntity>(GlobalStoreName.STICKY_BANNERS);

  private get _store() {
    return this._stickyBannersStore.storeSignal();
  }

  /**
   * Adds a banner to the queue and triggers opening.
   *
   * If the banner is already active or already queued, its data in the store is refreshed
   * instead of dropping the new payload — this is what lets re-fetched CMS content (e.g. after
   * a language switch) reach the store rather than being discarded by an early return.
   */
  public enqueue(item: StickyModalQueueItem): void {
    const store = this._store;

    const queuedIndex = store.stickyModalQueue.findIndex((queueItem) => queueItem.key === item.key);
    const isActiveOrQueued = store.activeStickyModalKey === item.key || queuedIndex !== -1;

    if (isActiveOrQueued) {
      if (queuedIndex !== -1) {
        const updatedQueue = [...store.stickyModalQueue];
        updatedQueue[queuedIndex] = item;
        this._patchStore({ stickyModalQueue: updatedQueue });
      }
      return;
    }

    this._patchStore({
      stickyModalQueue: [...store.stickyModalQueue, item],
    });

    void this._openNext();
  }

  /** Returns the raw config for a given banner key, or undefined if not registered. */
  public getConfig(key: string): StickyModalConfig | undefined {
    return this._store.stickyModalConfigs[key];
  }

  /** Resolves a config via a store-level alias key. Throws if the config is not registered. */
  public getConfigByStoreKey(key: StickyModalConfigKey): StickyModalConfig {
    const configKey = this._store.stickyModalKeys[key];
    const config = this.getConfig(configKey);

    if (!config) {
      throw new Error(`Sticky banner config "${key}" is not registered`);
    }

    return config;
  }

  /** Opens the next queued banner. No-ops if another banner is already active or the queue is empty. */
  private async _openNext(): Promise<void> {
    const store = this._store;

    if (store.activeStickyModalKey || !store.stickyModalQueue.length) {
      return;
    }

    const item = store.stickyModalQueue[0];
    const config = store.stickyModalConfigs[item.key];

    if (!config) {
      this._dropActiveItem();
      return;
    }

    this._patchStore({
      activeStickyModalKey: item.key,
    });

    this._customNotify.resolvePersonalizedNotification(item);

    try {
      const modalRef = await this._modal.openLazy(config.component, {
        template: 'BOTTOM',
        hideOverlay: true,
        isUnlockScroll: true,
        ...config.modalOptions,
        data: {
          banner: item.data[0],
          bonus: item.data[1],
          analytics: config.analytics,
          timerCookie: config.timerCookie,
        },
      });

      if (!modalRef) {
        this._dropActiveItem();
        return;
      }

      modalRef.onResult().subscribe((result) => this._handleClosed(item, config, result));
    } catch {
      this._dropActiveItem();
    }
  }

  /** Called when a modal closes. Marks the banner as displayed if confirmed, then opens the next one after the configured delay. */
  private _handleClosed(item: StickyModalQueueItem, config: StickyModalConfig, result: any): void {
    this._dropActiveItem();

    if (result || config.markDisplayedOnClose) {
      this._markDisplayed(item, config);
    }

    if (this._store.stickyModalQueue.length) {
      timer(config.delayAfterCloseMs ?? 0)
        .pipe(tap(() => void this._openNext()))
        .subscribe();
    }
  }

  /** Removes the first item from the queue and clears the active banner key. */
  private _dropActiveItem(): void {
    const store = this._store;

    this._patchStore({
      stickyModalQueue: store.stickyModalQueue.slice(1),
      activeStickyModalKey: null,
    });
  }

  /** Sets cookies to prevent the banner from showing again (config-level, data-level, and any extra cookies). */
  private _markDisplayed(item: StickyModalQueueItem, config: StickyModalConfig): void {
    const hostname = (window as any)?.location?.hostname;

    if (config.displayCookie) {
      this._cookie.set(config.displayCookie, '1', config.cookieExpires, '/', hostname);
    }

    const dataCookie = item.data?.bannerDisplayCookie;
    if (dataCookie) {
      this._cookie.set(dataCookie, '1', config.cookieExpires, '/', hostname);
    }

    this._getAdditionalCookies(item, config).forEach((cookie) => {
      if (cookie?.name) {
        this._cookie.set(cookie.name, cookie?.value, cookie?.expire, '/', hostname);
      }
    });
  }

  /** Merges extra cookies from config and item data; config takes priority. */
  private _getAdditionalCookies(item: StickyModalQueueItem, config: StickyModalConfig): StickyModalQueueCookie[] {
    return config.additionalCookies ?? item.data?.additionalCookieAfterCloseModal ?? [];
  }

  /** Shallow-merges a partial state patch into the store. */
  private _patchStore(patch: Partial<StickyBannersState>): void {
    this._stickyBannersStore.storeSignal.update((store) => ({ ...store, ...patch }));
  }
}
